import Image from 'next/image'
import React, { useRef, useState, useCallback, useMemo } from 'react'
import TagButton from '../../home-page/home-page-widgets/TagButton'
import ButtonWithArrow from '../../home-page/home-page-widgets/ButtonWithArrow'
import QuestionSection from '@/components/widgets/QuestionSection'
import DesignDetailsForm from './DesignDetailsForm'
import CalendlyEmbed from '@/components/widgets/CalendlyEmbed'
import Loading from '@/components/widgets/Loading'
import { useUser } from '@/components/widgets/UserProvider'
import VertelierExperienceEngagement from '@/components/widgets/VertelierExperienceEngagement'

// Constants
const VIDEO_SOURCES = {
  desktop: 'https://vert-test-assets.nyc3.cdn.digitaloceanspaces.com/website/Customization/119R.25375.mp4',
  mobile: 'https://vert-test-assets.nyc3.cdn.digitaloceanspaces.com/website/Customization/119R.25375.mp4'
} as const;

const IMAGE_SOURCES = {
  banner: {
    desktop: 'https://vert-test-assets.nyc3.cdn.digitaloceanspaces.com/website/Customization/customization_banner_web.webp',
    mobile: 'https://vert-test-assets.nyc3.cdn.digitaloceanspaces.com/website/Customization/customization_banner_mob.webp'
  },
  createJewelry: 'https://vert-test-assets.nyc3.cdn.digitaloceanspaces.com/website/Customization/create_your_jewelry.webp',
  trustSeal: 'https://vert-test-assets.nyc3.cdn.digitaloceanspaces.com/website/Customization/Vertelier_trust_seal.png',
  banner1: 'https://vert-test-assets.nyc3.cdn.digitaloceanspaces.com/website/Customization/Banner_image_1.png',
  banner2: 'https://vert-test-assets.nyc3.cdn.digitaloceanspaces.com/website/Customization/banner_image_2.png',
  signatureStyle: 'https://vert-test-assets.nyc3.cdn.digitaloceanspaces.com/website/Customization/Signature_Style.png'
} as const;

const HeroSection: React.FC = () => {
  const [isOpen, setOpen] = useState(false)
  const { userPageLoading } = useUser()
  const uploadSectionRef = useRef<HTMLDivElement>(null)

  const scrollToUploadSection = useCallback(() => {
    uploadSectionRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    })
  }, [])

  const handleOpenModal = useCallback(() => {
    setOpen(true)
  }, [])

  const handleCloseModal = useCallback(() => {
    setOpen(false)
  }, [])

  const renderBanner = useMemo(() => (
    <div className='bg-black'>
      {userPageLoading ? (
        <Loading classes='flex-grow flex relative max-h-screen sm:min-h-[645px] bg-[rgba(128,128,128,0.3)] !text-white' />
      ) : (
        <>
          <div className='relative h-[608px] switch:h-[608px] hidden md:block'>
            <Image
              src={IMAGE_SOURCES.banner.desktop}
              className='w-full h-full'
              alt='Customization banner'
              fill
              priority
              sizes="100vw"
            />
            <div className='absolute top-0 flex flex-col justify-center bottom-0 right-0 h-full w-full'>
              <h1 className='absolute bottom-0 w-full flex justify-center font-cambonRegular text-heading mb-lg:text-[26px] sm:text-[28px] switch:text-[34px] md:text-[40px] lg:text-[122px] text-[#fff] !tracking-[0.8px]'>
                Custom
              </h1>
            </div>
          </div>

          <div className='relative switch:h-[663px] md:hidden'>
            <Image
              src={IMAGE_SOURCES.banner.mobile}
              alt='customization page top banner'
              className='w-full h-full object-cover'
              fill
              priority
              sizes="100vw"
            />
            <div className='absolute flex flex-col bottom-0 right-0 h-full w-full'>
              <h1 className='h-full flex justify-center items-end font-cambonRegular text-[#FFF] text-[39px] leading-[89.4px] font-[400]'>
                Custom
              </h1>
            </div>
          </div>
        </>
      )}
    </div>
  ), [userPageLoading])

  const renderVisionSection = useMemo(() => (
    <div className='flex flex-col space-y-2 min-h-[534px] switch:space-y-5 justify-center items-center bg-gradient-to-br switch:bg-gradient-to-r from-[#041510] to-[#177B5E] py-10'>
      <Image
        src={IMAGE_SOURCES.trustSeal}
        alt='Vertelier trust seal'
        width={187}
        height={182}
        className='w-[137px] switch:w-[187px] h-auto object-center'
        priority
      />
      <h1 className='font-aesthetic text-heading pt-5 mb-lg:text-[26px] sm:text-[28px] switch:text-[34px] md:text-[40px] text-white switch:text-white lg:text-[64px] uppercase text-center'>
        Our Craftsman&apos;s Vision
      </h1>
      <p className='font-inter-400 text-body mb-lg:text-[16px] sm:text-[18px] px-[3%] md:px-[1%] lg:text-[20px] text-center max-w-[371px] mb-lg:max-w-[911px]'>
        Our jewelry reflects extraordinary care and meticulous craftsmanship, all backed by our Seal of Trust. Each piece is hand-crafted with dedication by our artisans in Kolkata, ensuring exceptional quality and attention to detail.
      </p>
    </div>
  ), [])

  const renderVideoSection = useMemo(() => (
    <div className='bg-black'>
      {userPageLoading ? (
        <Loading classes='flex-grow flex relative max-h-screen sm:min-h-[645px] mt-[10px] bg-[rgba(128,128,128,0.3)] !text-white' />
      ) : (
        <>
          <div className='relative md:h-[608px] hidden md:block'>
            <video
              className='w-full h-full object-cover'
              autoPlay
              muted
              loop
              playsInline
              onContextMenu={(e) => e.preventDefault()}
            >
              <source src={VIDEO_SOURCES.desktop} type='video/mp4' />
              Your browser does not support the video tag.
            </video>
            <div className='absolute bottom-[20px] right-0 flex justify-end mr-[50px]'>
              <div>
                <div className='text-[#FFFFFF] uppercase font-barlowSemibold text-[16px] leading-[19.2px] tracking-[2.4px] text-end'>
                  Jewels of Timeless Devotion
                </div>
                <div className='text-[#FFFFFF] uppercase text-end mt-[8px] font-barlowNormal text-[48px] leading-[57.6px] tracking-[1.6px]'>
                  <span className='font-barlowSemibold text-[48px] leading-[57.6px] tracking-[1.6px]'>
                    Custom Bridal
                  </span>{' '}
                  Reverie
                </div>
                <div className='mt-[20px] flex justify-end'>
                  <TagButton
                    onClick={handleOpenModal}
                    text='TALK TO OUR EXPERTS'
                    color='#A08260'
                    textColor={'#fff'}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className='!min-h-[503px] md:hidden'>
            <video
              className='w-full h-[300px] object-cover'
              autoPlay
              muted
              loop
              playsInline
              onContextMenu={(e) => e.preventDefault()}
            >
              <source src={VIDEO_SOURCES.mobile} type='video/mp4' />
              Your browser does not support the video tag.
            </video>
            <div className='flex justify-center'>
              <div>
                <div className='text-[#FFFFFF] uppercase font-barlowSemibold text-[10px] leading-[19.2px] tracking-[2.4px] text-center'>
                  Jewels of Timeless Devotion
                </div>
                <div className='text-[#FFFFFF] uppercase text-center font-barlowNormal text-[24px] tracking-[1.6px]'>
                  <span className='font-barlowSemibold text-[24px] leading-[57.6px] tracking-[1.6px]'>
                    Custom Bridal
                  </span>{' '}
                  Reverie
                </div>
                <div className='mt-[20px] flex justify-center'>
                  <TagButton
                    onClick={handleOpenModal}
                    text='TALK TO OUR EXPERTS'
                    color='#A08260'
                    textColor={'#fff'}
                  />
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  ), [userPageLoading, handleOpenModal])

  const renderModal = useMemo(() => (
    isOpen && (
      <div className='fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50'>
        <div className='bg-white rounded-lg shadow-lg relative lg:w-[60%] w-[90%]'>
          <button
            onClick={handleCloseModal}
            className='absolute top-3 right-3 bg-gray-200 p-1 rounded-full hover:bg-gray-300 transition-colors'
            aria-label="Close modal"
          >
            ✖
          </button>
          <br />
          <CalendlyEmbed />
        </div>
      </div>
    )
  ), [isOpen, handleCloseModal])

  if (userPageLoading) {
    return (
      <div className='flex flex-col space-y-4'>
        <Loading classes='flex-grow flex relative max-h-screen sm:min-h-[645px] bg-[rgba(128,128,128,0.3)] !text-white' />
        <Loading classes='flex-grow flex relative max-h-screen sm:min-h-[645px] bg-[rgba(128,128,128,0.3)] !text-white' />
      </div>
    )
  }

  return (
    <div>
      {renderBanner}
      {renderVisionSection}
      {renderVideoSection}
      {renderModal}

      {/* Rest of the component sections */}
      <div className='grid grid-cols-1 md:grid-cols-[1.2fr_1fr] my-0 md:my-section'>
        {/* Create your jewelry section */}
        {userPageLoading ? (
          <>
            <Loading
              classes={
                'flex-grow flex relative max-h-screen h-[615px] !bg-[rgba(128,128,128,0.3)] !text-white rounded-none'
              }
            />
          </>
        ) : (
          <div className='hidden md:block relative flex-1 h-[305px] switch:h-auto'>
            <Image
              width={796}
              height={640}
              alt='Image'
              src={IMAGE_SOURCES.createJewelry}
              className='h-full w-full flex-1 md:h-[640px] object-cover'
              loading="lazy"
            />
            <div className=' w-full h-full absolute top-0 left-0 right-0 bottom-0 flex flex-col justify-end lg:h-[100%]'>
              <div className=' ms-[3%] mb-5 md:mb-[78px] space-y-2 switch:space-y-5 lg:ml-[58px]'>
                <h1 className='font-cambonRegular text-heading mb-lg:text-[26px] sm:text-[28px] switch:text-[40px] leading-[30px] md:text-[64px] lg:text-[96px] switch:leading-[40px] md:leading-[70px]  lg:leading-[104px]'>
                  Create <br />
                  <span className='font-cambonLight'>
                    your{' '}
                    <span className='font-cambonLightItalic'>jewelry</span>
                  </span>
                </h1>
              </div>
            </div>
          </div>
        )}

        <div className='md:hidden relative h-[305px] switch:h-auto'>
          <img
            width={430}
            height={305}
            alt='image'
            src={IMAGE_SOURCES.createJewelry}
            className='h-full w-full object-cover'
            loading="lazy"
          />
          <div className=' w-full h-full absolute bottom-0 items-end flex justify-center'>
            <div className=' ms-[3%] mb-5 md:mb-32 space-y-2 switch:space-y-5'>
              <h1 className='font-cambonLight text-body mb-lg:text-[26px] sm:text-[28px] switch:text-[40px] leading-[30px] md:text-[64px] lg:text-[96px] switch:leading-[40px] md:leading-[70px]  lg:leading-[104px]'>
                Create <br />
                <span className='font-cambonLight'>
                  your <span className='font-cambonLightItalic'>jewelry</span>
                </span>
              </h1>
            </div>
          </div>
        </div>

        <div
          style={{
            background: `linear-gradient(139.4deg, #011D15 27.5%, #012D20 54.79%, #011D15 77.12%)`,
          }}
          className='overflow-hidden px-[3%] block md:hidden'
        >
          <div className='mt-[16px] flex justify-center md:hidden'>
            <span className='font-barlowNormal font-[300] text-white text-[12px] tracking-[1.6px] uppercase'>
              Our{' '}
              <span className='font-barlowSemibold font-[500] text-[12px] uppercase tracking-[1.6px]'>
                Approach
              </span>
            </span>
          </div>
          <div className='mt-[32px]'>
            <div className='flex justify-center items-center'>
              <div className='w-[22px] md:w-[30px] h-[15px] md:h-[18.03px]'>
                <Image
                  width={100}
                  height={100}
                  alt='design'
                  src={'/custom_design_icon.png'}
                  className='h-full w-full object-cover'
                />
              </div>
              <span className='font-barlowSemibold font-[500] text-[20px] md:text-[24px] leading-[28.8px] tracking-[1.6px] text-[#FFFFFF]'>
                Design Submission
              </span>
            </div>

            <div className='mt-[8px] text-center'>
              <span className='font-barlowMedium text-[14px] md:text-[16px] leading-[24px] text-[#FFFFFF]'>
                We transform your vision into reality with custom sketches,
                ensuring quality, durability, and budget.{' '}
                <span className='font-barlowBold'>Receive 3-5 designs</span>
                &nbsp; for feedback and revisions.
              </span>
            </div>
          </div>

          <div className='my-[48px]'>
            <div className='flex justify-center items-center'>
              <div className='w-[22px] md:w-[30px] h-[15px] md:h-[18.03px]'>
                <Image
                  width={100}
                  height={100}
                  alt='design'
                  src={'/custom_design_icon.png'}
                  className='h-full w-full object-cover'
                />
              </div>
              <span className='font-barlowSemibold font-[500] text-[20px] md:text-[24px] leading-[28.8px] tracking-[1.6px] text-[#FFFFFF]'>
                3D rendering
              </span>
            </div>

            <div className='mt-[8px] text-center'>
              <span className='font-barlowMedium text-[14px] md:text-[16px] leading-[24px] text-[#FFFFFF]'>
                We transform your vision into reality with custom sketches,
                ensuring quality, durability, and budget. Receive{' '}
                <span className='font-barlowBold'>
                  3-5 designs for feedback and revisions.
                </span>
              </span>
            </div>
          </div>

          <div className='mb-[48px]'>
            <div className='flex justify-center items-center'>
              <div className='w-[22px] md:w-[30px] h-[15px] md:h-[18.03px]'>
                <Image
                  width={100}
                  height={100}
                  alt='design'
                  src={'/custom_design_icon.png'}
                  className='h-full w-full object-cover'
                />
              </div>
              <span className='font-barlowSemibold font-[500] text-[20px] md:text-[24px] leading-[28.8px] tracking-[1.6px] text-[#FFFFFF]'>
                Casting & Finishing
              </span>
            </div>

            <div className='mt-[8px] text-center'>
              <span className='font-barlowMedium text-[14px] md:text-[16px] leading-[24px] text-[#FFFFFF]'>
                As soon as we receive your approval, our Master Craftsmen cast
                your ring in the{' '}
                <span className='font-barlowBold'>metal of your choice.</span>{' '}
                After your custom piece of jewelry is cast, our expert
                jewelers set{' '}
                <span className='font-barlowBold'>
                  your choice of diamonds or gemstones.
                </span>
              </span>
            </div>
          </div>

          <div className='w-full flex justify-center'>
            <div className='flex flex-col justify-center gap-[24px] mb-[32px]'>
              <TagButton
                onClick={scrollToUploadSection}
                text='submit your designs'
              />
              <ButtonWithArrow
                onClick={handleOpenModal}
                title='Book an Appointment'
                minSize={18}
                font='font-barlowMedium'
              />
            </div>
          </div>
        </div>

        {userPageLoading ? (
          <>
            <Loading
              classes={
                'flex-grow flex relative max-h-screen h-[615px] !bg-[rgba(128,128,128,0.3)] !text-white rounded-none'
              }
            />
          </>
        ) : (
          <div
            style={{
              background: `linear-gradient(139.4deg, #011D15 27.5%, #012D20 54.79%, #011D15 77.12%)`,
            }}
            className='overflow-hidden px-[3%] hidden md:block'
          >
            <div className='mt-[58px] ml-[36px]'>
              <div className='flex items-center'>
                <div className='w-[30px] h-[18.03px]'>
                  <Image
                    width={100}
                    height={100}
                    alt='design'
                    src={'/custom_design_icon.png'}
                    className='h-full w-full object-cover'
                  />
                </div>
                <span className='font-barlowSemibold font-[500] text-[24px] leading-[28.8px] tracking-[1.6px] text-[#FFFFFF]'>
                  Design Submission
                </span>
              </div>

              <div className='mt-[8px] ml-[30px]'>
                <span className='font-barlowMedium text-[16px] leading-[24px] text-[#FFFFFF]'>
                  We transform your vision into reality with custom sketches,
                  ensuring quality, durability, and budget. Receive 3-5
                  designs for feedback and revisions.
                </span>
              </div>
            </div>

            <div className='my-[52px] ml-[36px]'>
              <div className='flex items-center'>
                <div className='w-[30px] h-[18.03px]'>
                  <Image
                    width={100}
                    height={100}
                    alt='design'
                    src={'/custom_design_icon.png'}
                    className='h-full w-full object-cover'
                  />
                </div>
                <span className='font-barlowSemibold font-[500] text-[24px] leading-[28.8px] tracking-[1.6px] text-[#FFFFFF]'>
                  3D rendering
                </span>
              </div>

              <div className='mt-[8px] ml-[30px]'>
                <span className='font-barlowMedium text-[16px] leading-[24px] text-[#FFFFFF]'>
                  We transform your vision into reality with custom sketches,
                  ensuring quality, durability, and budget. Receive 3-5
                  designs for feedback and revisions.
                </span>
              </div>
            </div>

            <div className='mb-[52px] ml-[36px]'>
              <div className='flex items-center'>
                <div className='w-[30px] h-[18.03px]'>
                  <Image
                    width={100}
                    height={100}
                    alt='design'
                    src={'/custom_design_icon.png'}
                    className='h-full w-full object-cover'
                  />
                </div>
                <span className='font-barlowSemibold font-[500] text-[24px] leading-[28.8px] tracking-[1.6px] text-[#FFFFFF]'>
                  Casting & Finishing
                </span>
              </div>

              <div className='mt-[8px] ml-[30px]'>
                <span className='font-barlowMedium text-[16px] leading-[24px] text-[#FFFFFF]'>
                  As soon as we receive your approval, our Master Craftsmen
                  cast your ring in the metal of your choice. After your
                  custom piece of jewelry is cast, our expert jewelers set
                  your choice of diamonds or gemstones.
                </span>
              </div>
            </div>

            <div className='ml-[36px] flex gap-[24px]'>
              <TagButton
                onClick={scrollToUploadSection}
                text='submit your designs'
              />
              <TagButton
                onClick={handleOpenModal}
                text='book an appointment'
              />
            </div>
          </div>
        )}
      </div>

      {/* 3D rendering section */}
      {userPageLoading ? (
        <>
          <Loading
            classes={
              'flex-grow flex relative max-h-screen sm:min-h-[40px] mx-[40%] bg-[rgba(128,128,128,0.3)] !text-white'
            }
          />

          <Loading
            classes={
              'flex-grow flex relative max-h-screen sm:min-h-[80px] mt-[10px] mx-[35%] bg-[rgba(128,128,128,0.3)] !text-white'
            }
          />

          <Loading
            classes={
              'flex-grow flex relative max-h-screen sm:min-h-[50px] mt-[10px] mx-[40%] bg-[rgba(128,128,128,0.3)] !text-white'
            }
          />
        </>
      ) : (
        <>
          <div className='h-[300px] w-full relative md:h-[559px] flex justify-between items-center'>
            <img
              width={437}
              height={560}
              alt='design'
              src={IMAGE_SOURCES.banner1}
              className='sm:h-full h-[300px] sm:w-[30%] w-[50%] object-cover top-0 left-0 '
              loading="lazy"
            />
            <div className='z-10 absolute bg-black opacity-100 image-overlay-animation'></div>

            <div className='w-[40%] z-20 relative'>
              <div className='z-[99] relative sm:block hidden'>
                <div className='flex justify-center items-center'>
                  <span className='font-barlowSemibold uppercase text-[32px] leading-[38.4px] tracking-[1.6px] text-[#FFFFFF]'>
                    Opulent
                    <span className='font-barlowMedium uppercase text-[32pc leading-[38.8px] tracking-[1.6px]  text-[#FFFFFF]'>
                      Colors
                    </span>
                  </span>
                </div>

                <div className='flex justify-center'>
                  <div className='mt-[16px] mb-[24px] w-full text-center'>
                    <span className='font-barlowMedium text-[16px] leading-[19.2px] text-[#FFFFFF]'>
                      Like a tapestry of color, these exclusive earrings unite
                      opulent diamonds with the mystique of Lapis Lazuli. At
                      Vertelier, each piece embodies our unwavering pursuit of
                      artistic excellence—leaving an unforgettable impression at
                      every glance.
                    </span>
                  </div>
                </div>

                <div className='flex justify-center'>
                  <ButtonWithArrow title="Let&apos;s Get Started!" minSize={24} />
                </div>
              </div>
              <div className='absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-black sm:w-[187%] w-[235px] sm:h-[560px] h-[300px] shadow-[0_0_84px_rgba(0,0,0,1)]' />
            </div>

            <img
              width={437}
              height={560}
              alt='design'
              src={IMAGE_SOURCES.banner2}
              className='sm:h-full h-[300px] sm:w-[30%] w-[50%] object-cover  top-0 right-0 '
              loading="lazy"
            />
          </div>
          <div className='z-[99] relative sm:hidden block top-[100px] bg-black px-5'>
            <div className='flex justify-center items-center'>
              <span className='font-barlowSemibold uppercase text-[32px] leading-[38.4px] tracking-[1.6px] text-[#FFFFFF]'>
                Opulent&nbsp;
                <span className='font-barlowMedium uppercase text-[32pc leading-[38.8px] tracking-[1.6px]  text-[#FFFFFF]'>
                  Colors
                </span>
              </span>
            </div>

            <div className='flex justify-center'>
              <div className='mt-[16px] mb-[24px] w-full text-center'>
                <span className='font-barlowMedium text-[12px] md:text-[16px] leading-[19.2px] text-[#FFFFFF]'>
                  Like a tapestry of color, these exclusive earrings unite
                  opulent diamonds with the mystique of Lapis Lazuli. At
                  Vertelier, each piece embodies our unwavering pursuit of
                  artistic excellence—leaving an unforgettable impression at
                  every glance.
                </span>
              </div>
            </div>

            <div className='flex justify-center'>
              <ButtonWithArrow title="Let&apos;s Get Started!" minSize={24} />
            </div>
          </div>
        </>
      )}

      {/* Upload section */}
      <div ref={uploadSectionRef}>
        {userPageLoading ? (
          <Loading
            classes={
              'flex-grow flex relative max-h-screen h-[50px] mx-[20%] mt-[50px] !bg-[rgba(128,128,128,0.3)] !text-white'
            }
          />
        ) : (
          <div className='text-center mb-[36px] text-[#A08260] uppercase mt-[140px] md:mt-0 flex justify-center'>
            <div className='w-[70%] md:w-full'>
              <span className='font-barlowMedium text-[24px] md:text-[48px] leading-[28.8px] md:leading-[56px] tracking-[1.6px]'>
                Curate Your&nbsp;
                <span className='font-barlowSemibold text-[24px] md:text-[48px] leading-[28.8px] md:leading-[56px] tracking-[1.6px]'>
                  Signature Style
                </span>
              </span>
            </div>
          </div>
        )}
        {userPageLoading ? (
          <div className='justify-center flex m-[40px]'>
            <Loading
              classes={
                'flex-grow flex relative max-h-screen h-[585px] mr-[10px] !bg-[rgba(128,128,128,0.3)] !text-white rounded-none'
              }
            />

            <Loading
              classes={
                'flex-grow flex relative max-h-screen h-[585px] !bg-[rgba(128,128,128,0.3)] !text-white rounded-none'
              }
            />
          </div>
        ) : (
          <div className=' switch:px-10 lg:px-20 pb-20 grid md:flex bg-black items-center'>
            <div className='sm:w-[675px] w-full'>
              <img
                width={490}
                height={480}
                alt='any'
                src={IMAGE_SOURCES.signatureStyle}
                loading="lazy"
                className=' h-[480px] w-full object-cover'
              />
            </div>

            <div className=' sm:bg-[#013123] bg-transparent sm:px-[0px] px-[16px] switch:px-10 lg:px-20 pt-20 pb-5 min-h-[640px] w-full block'>
              <DesignDetailsForm />
            </div>
          </div>
        )}
      </div>

      {/* Vertelier experience */}
      <div className='flex w-full justify-center'>
        <VertelierExperienceEngagement
          normalTitle='Vertelier'
          italicTitle='Promises'
          page='home'
        />
      </div>

      {/* Question section */}
      <QuestionSection page='customization' />
    </div>
  )
}

export default HeroSection
